import type { BoxProps } from '@hover/blueprint';
import { HStack, useMediaQuery, useSize } from '@hover/blueprint';
import { useScroll } from 'framer-motion';
import React, { useEffect, useRef } from 'react';

import { CarouselArrow } from 'src/modules/homeownerApp/components/CarouselArrow/CarouselArrow';
import { PeekingCarouselElement } from 'src/modules/homeownerApp/components/PeekingCarousel/PeekingCarouselElement';
import { useScrollableLeftAndRight } from 'src/modules/homeownerApp/hooks';
import { hideScrollbarsStyles } from 'src/shared/utils/styles';

type PeekingCarouselProps = {
  setCurrentIndex: (index: number) => void;
  children: React.ReactElement[];
  peekValueOverrides?: { base?: number; tablet?: number; desktop?: number };
} & BoxProps;

export const PeekingCarousel = React.memo(
  ({ children, setCurrentIndex, ...props }: PeekingCarouselProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const isScrolling = useRef<NodeJS.Timeout | null>(null);
    const { scrollX } = useScroll({ container: ref });
    const {
      canScrollLeft: showLeftArrow,
      canScrollRight: showRightArrow,
      scroll,
    } = useScrollableLeftAndRight({ ref });
    const size = useSize(ref);
    const width = size?.width ?? 0;
    const [isTouchDevice] = useMediaQuery('(hover: none)');

    useEffect(() => {
      const element = ref.current;

      if (!element) return () => {};

      const handleScroll = () => {
        if (isScrolling.current) {
          clearTimeout(isScrolling.current);
        }

        isScrolling.current = setTimeout(() => {
          // Calculate the current index based on the scroll position
          const index = Math.round(scrollX.get() / width);
          setCurrentIndex(index);
        }, 200);
      };

      element.addEventListener('scroll', handleScroll);

      return () => {
        element.removeEventListener('scroll', handleScroll);
        if (isScrolling.current) {
          clearTimeout(isScrolling.current);
        }
      };
    }, [scrollX, setCurrentIndex, width, ref]);

    if (!children) return null;

    return (
      <HStack
        overflowX="auto"
        scrollBehavior="smooth"
        scrollSnapType="x mandatory"
        {...props}
        ref={ref}
        sx={hideScrollbarsStyles()}
      >
        {children.map((child, index) => (
          <PeekingCarouselElement
            containerWidth={size?.width ?? 0}
            index={index}
            isLast={index === children.length - 1}
            key={child.key}
            peekValueOverrides={props.peekValueOverrides}
            scrollX={scrollX}
          >
            {child}
          </PeekingCarouselElement>
        ))}

        <CarouselArrow
          direction="previous"
          left={800}
          onClick={() => scroll('previous')}
          position="absolute"
          shouldShow={!isTouchDevice && showLeftArrow}
        />
        <CarouselArrow
          direction="next"
          onClick={() => scroll('next')}
          position="absolute"
          right={800}
          shouldShow={!isTouchDevice && showRightArrow}
        />
      </HStack>
    );
  },
);
